import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import CustomModal from "../../components/CustomComponents/modal/customModal.jsx";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import PageSizeDropDownSelection, {
  SortOrderDropDownSelection,
} from "../../components/CustomComponents/DropdownSelections/DropDownSelections.jsx";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import swal from "sweetalert";
import "../quotes/QuoteFilters.css";

const emptyUploadForm = {
  platform: null,
  version: "",
  buildNumber: "",
  releaseNotes: "",
  isActive: false,
  isMandatory: false,
};

const emptyEditForm = {
  releaseID: 0,
  platform: "",
  version: "",
  buildNumber: "",
  releaseNotes: "",
  isActive: false,
  isMandatory: false,
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes <= 0) return "0 B";
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1);
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
};

const normalizeRelease = (item) => ({
  releaseID: item.release_ID ?? item.releaseID ?? item.Release_ID ?? 0,
  platform: item.platform ?? item.Platform ?? "",
  version: item.version ?? item.Version ?? "",
  buildNumber: item.buildNumber ?? item.BuildNumber ?? null,
  originalFileName: item.originalFileName ?? item.OriginalFileName ?? "",
  filePath: item.filePath ?? item.FilePath ?? "",
  fileSizeBytes: item.fileSizeBytes ?? item.FileSizeBytes ?? 0,
  fileExtension: item.fileExtension ?? item.FileExtension ?? "",
  releaseNotes: item.releaseNotes ?? item.ReleaseNotes ?? "",
  isActive: item.isActive ?? item.IsActive ?? false,
  isMandatory: item.isMandatory ?? item.IsMandatory ?? false,
  uploadedBy: item.uploadedBy ?? item.UploadedBy ?? "",
  createdDate: item.createdDate ?? item.CreatedDate ?? "",
  modifiedDate: item.modifiedDate ?? item.ModifiedDate ?? "",
});

const getApiErrorMessage = (errOrData, fallback = "Request failed") => {
  const data = errOrData?.response?.data ?? errOrData;
  if (typeof data === "string" && data.trim()) return data;
  if (data?.message || data?.Message) return data.message || data.Message;
  if (data?.title || data?.Title) {
    const errors = data.errors || data.Errors;
    if (errors && typeof errors === "object") {
      const details = Object.entries(errors)
        .flatMap(([key, msgs]) => (Array.isArray(msgs) ? msgs : [msgs]).map((m) => `${key}: ${m}`))
        .join("; ");
      if (details) return details;
    }
    return data.title || data.Title;
  }
  return errOrData?.message || fallback;
};

const ManagePosAppReleases = () => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [releases, setReleases] = useState([]);
  const [platformOptions, setPlatformOptions] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [uploadForm, setUploadForm] = useState(emptyUploadForm);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [selectedFile, setSelectedFile] = useState(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [platformFilter, setPlatformFilter] = useState({ value: "", label: "All Platforms" });
  const [activeFilter, setActiveFilter] = useState({ value: "all", label: "All Status" });
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState({ value: "CreatedDate", label: "Created Date" });
  const [sortOrder, setSortOrder] = useState({ value: "DESC", label: "Desc" });

  const pageSizeNumber = parseInt(pageSize.value, 10) || 50;

  const sortByOptions = [
    { value: "Release_ID", label: "ID" },
    { value: "Platform", label: "Platform" },
    { value: "Version", label: "Version" },
    { value: "CreatedDate", label: "Created Date" },
    { value: "ModifiedDate", label: "Modified Date" },
  ];

  const activeFilterOptions = [
    { value: "all", label: "All Status" },
    { value: "active", label: "Active Only" },
    { value: "inactive", label: "Inactive Only" },
  ];

  const platformFilterOptions = useMemo(
    () => [
      { value: "", label: "All Platforms" },
      ...platformOptions.map((p) => ({
        value: p.platform ?? p.Platform,
        label: p.platform ?? p.Platform,
      })),
    ],
    [platformOptions]
  );

  const selectedPlatformExtensions = useMemo(() => {
    const platform = uploadForm.platform?.value;
    const match = platformOptions.find((p) => (p.platform ?? p.Platform) === platform);
    return match?.allowedExtensions ?? match?.AllowedExtensions ?? [];
  }, [uploadForm.platform, platformOptions]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPageNumber(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchPlatforms = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_SA_POS_RELEASE_PLATFORMS);
      if (response.data.status) {
        setPlatformOptions(Array.isArray(response.data.data) ? response.data.data : []);
      }
    } catch {
      showErrorToast("Failed to load platform options");
    }
  }, []);

  const fetchReleases = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        Search: debouncedSearch.trim(),
        Platform: platformFilter.value || "",
        SortBy: sortBy.value,
        SortOrder: sortOrder.value,
        PageSize: pageSizeNumber,
        PageNumber: pageNumber,
      };

      if (activeFilter.value === "active") params.IsActive = true;
      if (activeFilter.value === "inactive") params.IsActive = false;

      const response = await axiosInstance.get(APIs.GET_SA_ALL_POS_RELEASES, { params });
      const isSuccess = response.data?.status === true || response.data?.Status === true;
      if (isSuccess) {
        const data = response.data.data || {};
        const list = data.releases ?? data.Releases ?? [];
        setReleases(Array.isArray(list) ? list.map(normalizeRelease) : []);
        setTotalRecords(data.totalRecords ?? data.TotalRecords ?? 0);
        setTotalPages(Math.max(data.totalPages ?? data.TotalPages ?? 0, 1));
      } else {
        setReleases([]);
        setTotalRecords(0);
        setTotalPages(1);
        showErrorToast(response.data?.message || response.data?.Message || "Failed to load releases");
      }
    } catch (err) {
      setReleases([]);
      setTotalRecords(0);
      setTotalPages(1);
      showErrorToast(getApiErrorMessage(err, "Failed to load releases"));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, platformFilter, activeFilter, sortBy, sortOrder, pageSizeNumber, pageNumber]);

  useEffect(() => {
    fetchPlatforms();
  }, [fetchPlatforms]);

  useEffect(() => {
    fetchReleases();
  }, [fetchReleases]);

  const openUploadModal = () => {
    setUploadForm(emptyUploadForm);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setShowUploadModal(true);
  };

  const openEditModal = (release) => {
    setEditForm({
      releaseID: release.releaseID,
      platform: release.platform,
      version: release.version,
      buildNumber: release.buildNumber ?? "",
      releaseNotes: release.releaseNotes ?? "",
      isActive: release.isActive,
      isMandatory: release.isMandatory,
    });
    setShowEditModal(true);
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files?.[0] || null);
  };

  const validateSelectedFile = () => {
    if (!selectedFile) {
      showErrorToast("Please select an installer file");
      return false;
    }
    if (!uploadForm.platform?.value) {
      showErrorToast("Please select a platform");
      return false;
    }
    const ext = selectedFile.name.substring(selectedFile.name.lastIndexOf(".")).toLowerCase();
    if (selectedPlatformExtensions.length > 0 && !selectedPlatformExtensions.includes(ext)) {
      showErrorToast(
        `Allowed extensions for ${uploadForm.platform.label}: ${selectedPlatformExtensions.join(", ")}`
      );
      return false;
    }
    return true;
  };

  const handleUpload = async () => {
    if (!uploadForm.version?.trim()) {
      showErrorToast("Version is required (e.g. 1.2.3)");
      return;
    }
    if (!validateSelectedFile()) return;

    if (uploadForm.buildNumber !== "" && uploadForm.buildNumber != null) {
      const parsedBuild = parseInt(uploadForm.buildNumber, 10);
      if (Number.isNaN(parsedBuild)) {
        showErrorToast("Build number must be a whole number");
        return;
      }
    }

    try {
      setUploading(true);
      const formData = new FormData();
      // Prefer ReleaseFile; backend also accepts File for compatibility
      formData.append("ReleaseFile", selectedFile, selectedFile.name);
      formData.append("Platform", uploadForm.platform.value);
      formData.append("Version", uploadForm.version.trim());
      if (uploadForm.buildNumber !== "" && uploadForm.buildNumber != null) {
        formData.append("BuildNumber", String(parseInt(uploadForm.buildNumber, 10)));
      }
      if (uploadForm.releaseNotes) formData.append("ReleaseNotes", uploadForm.releaseNotes);
      formData.append("IsActive", uploadForm.isActive ? "true" : "false");
      formData.append("IsMandatory", uploadForm.isMandatory ? "true" : "false");

      const response = await axiosInstance.post(APIs.POST_SA_UPLOAD_POS_RELEASE, formData, {
        timeout: 300000,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        headers: { "Content-Type": undefined },
      });

      const isSuccess = response.data?.status === true || response.data?.Status === true;
      if (isSuccess) {
        showSuccessToast(response.data?.message || response.data?.Message || "Release uploaded successfully");
        setShowUploadModal(false);
        setUploadForm(emptyUploadForm);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        fetchReleases();
      } else {
        showErrorToast(getApiErrorMessage(response.data, "Upload failed"));
      }
    } catch (err) {
      showErrorToast(getApiErrorMessage(err, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setLoading(true);
      const payload = {
        releaseID: editForm.releaseID,
        buildNumber: editForm.buildNumber === "" ? null : parseInt(editForm.buildNumber, 10),
        releaseNotes: editForm.releaseNotes,
        isActive: editForm.isActive,
        isMandatory: editForm.isMandatory,
      };

      const response = await axiosInstance.put(APIs.PUT_SA_UPDATE_POS_RELEASE, payload);
      if (response.data.status) {
        showSuccessToast("Release updated successfully");
        setShowEditModal(false);
        fetchReleases();
      } else {
        showErrorToast(response.data.message || "Update failed");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (release) => {
    const confirmed = await swal({
      title: "Delete release?",
      text: `${release.platform} v${release.version} will be removed permanently.`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!confirmed) return;

    try {
      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_SA_POS_RELEASE, {
        params: { ReleaseID: release.releaseID },
      });
      if (response.data.status) {
        showSuccessToast("Release deleted");
        fetchReleases();
      } else {
        showErrorToast(response.data.message || "Delete failed");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (release) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_SA_DOWNLOAD_POS_RELEASE, {
        params: { ReleaseID: release.releaseID },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", release.originalFileName || `release-${release.releaseID}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      showErrorToast("Failed to download release file");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setPlatformFilter({ value: "", label: "All Platforms" });
    setActiveFilter({ value: "all", label: "All Status" });
    setPageNumber(1);
  };

  const platformSelectOptions = platformOptions.map((p) => ({
    value: p.platform ?? p.Platform,
    label: p.platform ?? p.Platform,
  }));

  const tableHeaderStyle = {
    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
    color: isDarkMode ? "#ffffff" : "#000000",
    borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6",
  };

  return (
    <div className="content">
      {loading && <Preloader />}
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                    <Link to="/dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">App Releases</li>
                </ol>
                <h1 className="page-header mb-0">POS App Releases</h1>
              </div>
              <div className="ms-auto">
                <button
                  type="button"
                  className="btn px-4"
                  onClick={openUploadModal}
                  style={{
                    backgroundColor: themeColor,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    border: "none",
                    color: "white",
                    borderRadius: "25px",
                    fontWeight: "500",
                  }}
                >
                  <i className="fa fa-upload fa-lg me-2 ms-n2"></i>
                  Upload Release
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="mb-4">
                  <div
                    className="card filter-card"
                    style={{
                      position: "relative",
                      zIndex: 1,
                      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                      borderRadius: "12px",
                      border: "none",
                      boxShadow: `0 4px 20px ${themeColor}40`,
                    }}
                  >
                    <div className="card-body p-3">
                      <div className="row g-3 align-items-center">
                        <div className="col-xl-3 col-lg-4 col-md-6">
                          <input
                            type="text"
                            className="filter-input w-100"
                            placeholder="Search version or file..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{
                              backgroundColor: isDarkMode ? "rgba(45, 52, 54, 0.9)" : "rgba(255, 255, 255, 0.9)",
                              border: "1px solid rgba(255, 255, 255, 0.3)",
                              color: isDarkMode ? "#ffffff" : "#333",
                              borderRadius: "6px",
                              height: "36px",
                              padding: "0 12px",
                            }}
                          />
                        </div>
                        <div className="col-xl-2 col-lg-3 col-md-6">
                          <Select
                            styles={dropdownStyles}
                            options={platformFilterOptions}
                            value={platformFilter}
                            onChange={(opt) => {
                              setPlatformFilter(opt || { value: "", label: "All Platforms" });
                              setPageNumber(1);
                            }}
                            placeholder="Platform"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                          />
                        </div>
                        <div className="col-xl-2 col-lg-2 col-md-6">
                          <Select
                            styles={dropdownStyles}
                            options={activeFilterOptions}
                            value={activeFilter}
                            onChange={(opt) => {
                              setActiveFilter(opt || activeFilterOptions[0]);
                              setPageNumber(1);
                            }}
                            placeholder="Status"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                          />
                        </div>
                        <div className="col-xl-2 col-lg-2 col-md-6">
                          <Select
                            styles={dropdownStyles}
                            options={sortByOptions}
                            value={sortBy}
                            onChange={(opt) => {
                              setSortBy(opt || sortByOptions[3]);
                              setPageNumber(1);
                            }}
                            placeholder="Sort By"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                          />
                        </div>
                        <div className="col-xl-1 col-lg-2 col-md-6">
                          <SortOrderDropDownSelection
                            defaultValue={sortOrder}
                            onChange={(opt) => {
                              setSortOrder(opt);
                              setPageNumber(1);
                            }}
                            styles={dropdownStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                          />
                        </div>
                        <div className="col-xl-1 col-lg-2 col-md-6">
                          <PageSizeDropDownSelection
                            pageSize={pageSize}
                            setPageSize={(opt) => {
                              setPageSize(opt);
                              setPageNumber(1);
                            }}
                          />
                        </div>
                        <div className="col-xl-1 col-lg-2 col-md-6">
                          <button
                            type="button"
                            className="clear-btn w-100"
                            onClick={clearFilters}
                            title="Clear all filters"
                            style={{
                              height: "36px",
                              backgroundColor: isDarkMode ? "rgba(45, 52, 54, 0.8)" : "rgba(255, 255, 255, 0.2)",
                              border: "1px solid rgba(255, 255, 255, 0.3)",
                              color: "white",
                              borderRadius: "6px",
                            }}
                          >
                            <i className="fa fa-times me-1"></i>
                            Clear
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead style={{ position: "sticky", top: 0, zIndex: 5 }}>
                      <tr>
                        {["Platform", "Version", "Build #", "File", "Size", "Active", "Mandatory", "Uploaded", "Actions"].map(
                          (col) => (
                            <th key={col} className="pt-2 pb-2" style={tableHeaderStyle}>
                              {col}
                            </th>
                          )
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {!loading && releases.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="text-center py-5">
                            <i
                              className="fa fa-cloud-upload-alt mb-3 d-block"
                              style={{ fontSize: "40px", color: isDarkMode ? "#6c757d" : "#adb5bd" }}
                            ></i>
                            <p className="mb-2" style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                              No POS app releases yet
                            </p>
                            <p className="small mb-3" style={{ color: isDarkMode ? "#868e96" : "#868e96" }}>
                              Upload Windows, Android, iOS, or macOS installer builds for POS clients to download.
                            </p>
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={openUploadModal}
                              style={{
                                backgroundColor: themeColor,
                                borderColor: themeColor,
                                color: "#fff",
                                borderRadius: "20px",
                              }}
                            >
                              <i className="fa fa-plus me-1"></i>
                              Upload first release
                            </button>
                          </td>
                        </tr>
                      ) : (
                        releases.map((release) => (
                          <tr key={release.releaseID}>
                            <td>
                              <span className="badge bg-dark">{release.platform}</span>
                            </td>
                            <td className="fw-semibold">{release.version}</td>
                            <td>{release.buildNumber ?? "-"}</td>
                            <td title={release.originalFileName}>
                              <div className="text-truncate" style={{ maxWidth: "180px" }}>
                                {release.originalFileName}
                              </div>
                              <div className="text-muted small">{release.fileExtension}</div>
                            </td>
                            <td>{formatFileSize(release.fileSizeBytes)}</td>
                            <td>
                              <span className={`badge ${release.isActive ? "bg-success" : "bg-secondary"}`}>
                                {release.isActive ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${release.isMandatory ? "bg-danger" : "bg-light text-dark"}`}>
                                {release.isMandatory ? "Yes" : "No"}
                              </span>
                            </td>
                            <td>
                              <div>{release.uploadedBy || "-"}</div>
                              <div className="text-muted small">
                                {release.createdDate ? new Date(release.createdDate).toLocaleString() : "-"}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex gap-2 flex-wrap">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => openEditModal(release)}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => handleDownload(release)}
                                >
                                  Download
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDelete(release)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="row mt-3">
                    <div className="col-12">
                      <Pagination
                        totalRecords={totalRecords}
                        pageSize={pageSizeNumber}
                        pageNumber={pageNumber}
                        totalPages={totalPages}
                        setPageNumber={setPageNumber}
                      />
                    </div>
                  </div>
                )}

                <div className="row mt-3">
                  <div className="col-12">
                    <p style={{ color: isDarkMode ? "#adb5bd" : "#6c757d", marginBottom: 0 }}>
                      Showing {releases.length} of {totalRecords} release(s)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CustomModal
        title="Upload POS Release"
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        isCentered={true}
      >
        <div className="mb-3">
          <label className="form-label">Platform</label>
          <Select
            styles={dropdownStyles}
            options={platformSelectOptions}
            value={uploadForm.platform}
            onChange={(opt) => setUploadForm({ ...uploadForm, platform: opt })}
            placeholder="Select platform..."
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
        <FormInput
          label="Version"
          placeHolder="e.g. 1.2.3"
          type="text"
          value={uploadForm.version}
          onchange={(e) => setUploadForm({ ...uploadForm, version: e.target.value })}
        />
        <FormInput
          label="Build Number (optional)"
          placeHolder="e.g. 42"
          type="number"
          value={uploadForm.buildNumber}
          onchange={(e) => setUploadForm({ ...uploadForm, buildNumber: e.target.value })}
        />
        <div className="mb-3">
          <label className="form-label">Release Notes</label>
          <textarea
            className="form-control"
            rows={3}
            value={uploadForm.releaseNotes}
            onChange={(e) => setUploadForm({ ...uploadForm, releaseNotes: e.target.value })}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Installer File</label>
          <input
            ref={fileInputRef}
            type="file"
            className="form-control"
            onChange={handleFileChange}
            accept={selectedPlatformExtensions.join(",") || undefined}
          />
          {selectedPlatformExtensions.length > 0 && (
            <small className="text-muted">
              Allowed: {selectedPlatformExtensions.join(", ")} (max 250MB)
            </small>
          )}
          {selectedFile && (
            <div className="mt-1 small">
              Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
            </div>
          )}
        </div>
        <div className="form-check mb-2">
          <input
            className="form-check-input"
            type="checkbox"
            id="uploadIsActive"
            checked={uploadForm.isActive}
            onChange={(e) => setUploadForm({ ...uploadForm, isActive: e.target.checked })}
          />
          <label className="form-check-label" htmlFor="uploadIsActive">
            Set as active release for this platform
          </label>
        </div>
        <div className="form-check mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="uploadIsMandatory"
            checked={uploadForm.isMandatory}
            onChange={(e) => setUploadForm({ ...uploadForm, isMandatory: e.target.checked })}
          />
          <label className="form-check-label" htmlFor="uploadIsMandatory">
            Mandatory update (force POS clients to update)
          </label>
        </div>
        <div className="d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={() => setShowUploadModal(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={uploading}
            style={{ backgroundColor: themeColor, borderColor: themeColor }}
          >
            {uploading ? "Uploading..." : "Upload"}
          </button>
        </div>
      </CustomModal>

      <CustomModal
        title={`Edit Release - ${editForm.platform} v${editForm.version}`}
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        isCentered={true}
      >
        <FormInput
          label="Build Number (optional)"
          placeHolder="e.g. 42"
          type="number"
          value={editForm.buildNumber}
          onchange={(e) => setEditForm({ ...editForm, buildNumber: e.target.value })}
        />
        <div className="mb-3">
          <label className="form-label">Release Notes</label>
          <textarea
            className="form-control"
            rows={3}
            value={editForm.releaseNotes}
            onChange={(e) => setEditForm({ ...editForm, releaseNotes: e.target.value })}
          />
        </div>
        <div className="form-check mb-2">
          <input
            className="form-check-input"
            type="checkbox"
            id="editIsActive"
            checked={editForm.isActive}
            onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
          />
          <label className="form-check-label" htmlFor="editIsActive">
            Active release for this platform
          </label>
        </div>
        <div className="form-check mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="editIsMandatory"
            checked={editForm.isMandatory}
            onChange={(e) => setEditForm({ ...editForm, isMandatory: e.target.checked })}
          />
          <label className="form-check-label" htmlFor="editIsMandatory">
            Mandatory update
          </label>
        </div>
        <div className="d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleUpdate}
            style={{ backgroundColor: themeColor, borderColor: themeColor }}
          >
            Save Changes
          </button>
        </div>
      </CustomModal>
    </div>
  );
};

export default ManagePosAppReleases;
